<template>
  <div>
    <DeleteModal modal-id="modal-delete" @deleteToggle="deleteItem" />

    <b-modal
      id="modal-dispose"
      :title="$t('bloodBank.dispose-confirmation')"
      ok-variant="danger"
      :ok-only="true"
    >
      <p>{{ $t("bloodBank.dispose-confirmation-message") }}</p>
      <template #modal-footer="{}">
        <b-button
          ref="button"
          variant="warning"
          class="m-0"
          :disabled="loading"
          block
          @click="disposeBottle"
        >
          <span v-if="!loading" style="font-weight: bold">{{
            $t("bloodBank.dispose")
          }}</span>
          <b-spinner v-else small />
        </b-button>
      </template>
    </b-modal>

    <b-modal
      id="modal-revert-disposal"
      :title="$t('bloodBank.revert-disposal-confirmation')"
      ok-variant="warning"
      :ok-only="true"
    >
      <p>{{ $t("bloodBank.revert-disposal-confirmation-message") }}</p>
      <template #modal-footer="{}">
        <b-button
          ref="button"
          variant="success"
          class="m-0"
          :disabled="loading"
          block
          @click="revertDisposal"
        >
          <span v-if="!loading" style="font-weight: bold">{{
            $t("bloodBank.revert-disposal")
          }}</span>
          <b-spinner v-else small />
        </b-button>
      </template>
    </b-modal>

    <b-modal
      id="modal-edit"
      :no-close-on-backdrop="true"
      ok-only
      centered
      size="lg"
      :title="
        createMode
          ? $t('bloodBank.add-blood-bottle')
          : $t('bloodBank.edit-blood-bottle')
      "
    >
      <b-card-text>
        <b-row>
          <b-col md="">
            <b-form-group
              :label="$t('bloodBank.bottle-identifier')"
              label-for="bottle_identifier"
              style="font-weight: bold"
            >
              <b-form-input
                v-model="EditForm.bottle_identifier"
                :placeholder="$t('bloodBank.bottle-identifier')"
                :readonly="!createMode"
                style="font-weight: bold"
              />
              <p class="text-danger mt-1">
                {{ errorForm.bottle_identifier }}
              </p>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="6">
            <b-form-group
              :label="$t('bloodBank.abo')"
              label-for="abo"
              style="font-weight: bold"
            >
              <v-select
                v-model="EditForm.abo"
                :options="aboOptions"
                placeholder="A, B, AB, O"
              />
              <p class="text-danger mt-1">
                {{ errorForm.abo }}
              </p>
            </b-form-group>
          </b-col>

          <b-col md="6">
            <b-form-group
              :label="$t('bloodBank.rh')"
              label-for="rh"
              style="font-weight: bold"
            >
              <v-select
                v-model="EditForm.rh"
                :options="rhOptions"
                placeholder="Positive, Negative"
              />
              <p class="text-danger mt-1">
                {{ errorForm.rh }}
              </p>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="12">
            <b-form-group
              :label="$t('bloodBank.inserted-at')"
              label-for="inserted_at"
              style="font-weight: bold"
            >
              <b-form-datepicker
                v-model="EditForm.inserted_at"
                :placeholder="$t('common.select-date')"
                :date-format-options="{
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                }"
                locale="en"
                class="dark-datepicker"
                today-button
                reset-button
                close-button
              />
            </b-form-group>
            <p class="text-danger mt-1">
              {{ errorForm.inserted_at }}
            </p>
          </b-col>
        </b-row>
        <b-row>
          <b-col md="6">
            <b-form-group
              :label="$t('bloodBank.prod-date')"
              label-for="production_date"
              style="font-weight: bold"
            >
              <b-form-datepicker
                v-model="EditForm.production_date"
                :placeholder="$t('common.select-date')"
                :date-format-options="{
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                }"
                locale="en"
                class="dark-datepicker"
                today-button
                reset-button
                close-button
              />
            </b-form-group>

            <p class="text-danger mt-1">
              {{ errorForm.production_date }}
            </p>
          </b-col>
          <b-col md="6">
            <b-form-group
              :label="$t('bloodBank.exp-date')"
              label-for="expiration_date"
              style="font-weight: bold"
            >
              <b-form-datepicker
                v-model="EditForm.expiration_date"
                :placeholder="$t('common.select-date')"
                :date-format-options="{
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                }"
                locale="en"
                class="dark-datepicker"
                today-button
                reset-button
                close-button
              />
            </b-form-group>

            <p class="text-danger mt-1">
              {{ errorForm.expiration_date }}
            </p>
          </b-col>
        </b-row>
      </b-card-text>
      <template #modal-footer="{}">
        <b-button
          ref="button"
          variant="primary"
          class="m-0"
          :disabled="loading"
          block
          @click="submitItem"
        >
          <span v-if="!loading">{{
            createMode ? $t("common.add") : $t("common.edit")
          }}</span>
          <b-spinner v-else small />
        </b-button>
      </template>
    </b-modal>

    <b-card no-body class="compact-card main-padding">
      <div class="compact-container">
        <!-- Header with title and view toggle -->
        <b-row class="compact-row">
          <b-col
            md="12"
            class="d-flex justify-content-between align-items-center compact-header"
          >
            <h2 class="mb-0 compact-title">
              {{ $t("bloodBank.blood-bottle-table") }}
            </h2>
            <div class="d-flex align-items-center gap-2" style="gap: 10px">
              <div class="view-toggle-buttons">
                <b-button-group size="sm">
                  <b-button
                    :variant="
                      viewMode === 'list' ? 'primary' : 'outline-primary'
                    "
                    size="sm"
                    @click="viewMode = 'list'"
                  >
                    <feather-icon icon="ListIcon" size="14" />
                    {{ $t("common.table") }}
                  </b-button>
                  <b-button
                    :variant="
                      viewMode === 'card' ? 'primary' : 'outline-primary'
                    "
                    size="sm"
                    @click="viewMode = 'card'"
                  >
                    <feather-icon icon="GridIcon" size="14" />
                    {{ $t("common.card") }}
                  </b-button>
                </b-button-group>
              </div>
              <b-button
                variant="primary"
                size="sm"
                @click="show(EditForm, 'create')"
              >
                <feather-icon icon="PlusIcon" size="14" />
                {{ $t("bloodBank.add-blood-bottle") }}
              </b-button>
            </div>
          </b-col>
        </b-row>

        <!-- Blood Balance Cards - Outside collapse -->
        <b-row
          v-if="balances.length > 0"
          class="compact-row compact-balance-cards"
        >
          <b-col
            v-for="balance in balances"
            :key="balance.abo"
            md="3"
            cols="6"
            class="compact-col"
          >
            <div class="blood-balance-card text-center compact-balance-card">
              <h3 class="mb-2 compact-balance-abo">
                {{ balance.abo }}
              </h3>
              <div
                class="d-flex justify-content-between align-items-center compact-balance-row"
              >
                <span class="compact-balance-label"
                  >{{ $t("lab.positive") }}:</span
                >
                <b-badge variant="success" class="compact-balance-badge">
                  {{ balance.Positive || 0 }}
                </b-badge>
              </div>
              <div
                class="d-flex justify-content-between align-items-center compact-balance-row"
              >
                <span class="compact-balance-label"
                  >{{ $t("lab.negative") }}:</span
                >
                <b-badge variant="danger" class="compact-balance-badge">
                  {{ balance.Negative || 0 }}
                </b-badge>
              </div>
            </div>
          </b-col>
        </b-row>

        <!-- Filters Section -->
        <b-row class="compact-row">
          <b-col md="12">
            <b-button
              v-b-toggle.filter-collapse
              variant="outline-primary"
              size="sm"
              class="compact-filter-toggle"
            >
              <feather-icon icon="FilterIcon" size="14" class="mr-50" />
              <span class="when-closed mr-50">{{
                $t("common.show-filters")
              }}</span>
              <span class="when-open mr-50">{{
                $t("common.hide-filters")
              }}</span>
              <feather-icon
                icon="ChevronDownIcon"
                size="14"
                class="when-closed"
              />
              <feather-icon icon="ChevronUpIcon" size="14" class="when-open" />
            </b-button>
          </b-col>
        </b-row>

        <b-collapse id="filter-collapse">
          <div class="compact-filters-section">
            <b-row class="compact-row compact-filters">
              <b-col md="3" cols="12" class="compact-col">
                <b-form-group
                  :label="$t('bloodBank.bottle-identifier')"
                  label-for="bottle_identifier"
                  class="compact-form-group"
                >
                  <b-form-input
                    v-model="bottle_identifier"
                    :placeholder="$t('bloodBank.bottle-identifier')"
                    class="compact-input"
                  />
                </b-form-group>
              </b-col>
              <b-col md="2" cols="6" class="compact-col">
                <b-form-group
                  :label="$t('bloodBank.abo')"
                  label-for="abo"
                  class="compact-form-group"
                >
                  <v-select
                    v-model="abo"
                    :options="aboOptions"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    placeholder="A, B, AB, O"
                    class="compact-input"
                  />
                </b-form-group>
              </b-col>
              <b-col md="2" cols="6" class="compact-col">
                <b-form-group
                  :label="$t('bloodBank.rh')"
                  label-for="rh"
                  class="compact-form-group"
                >
                  <v-select
                    v-model="rh"
                    :options="rhOptions"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    placeholder="Positive, Negative"
                    class="compact-input"
                  />
                </b-form-group>
              </b-col>
              <b-col md="2" cols="6" class="compact-col">
                <b-form-group
                  :label="$t('bloodBank.status')"
                  label-for="status"
                  class="compact-form-group"
                >
                  <v-select
                    v-model="status"
                    :options="statusOptions"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    placeholder="available, dispensed, disposed"
                    class="compact-input"
                  />
                </b-form-group>
              </b-col>
              <b-col md="3" cols="6" class="compact-col">
                <b-form-group
                  :label="$t('bloodBank.search-from-to-by')"
                  label-for="searchDate"
                  class="compact-form-group"
                >
                  <v-select
                    v-model="searchDate"
                    :options="searchFromToBy"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    label="label"
                    :reduce="(option) => option"
                    placeholder="Search From To By"
                    class="compact-input"
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <b-row
              v-if="selectedSearch && selectedSearch.key"
              class="compact-row compact-filters"
            >
              <b-col md="6" cols="12" class="compact-col">
                <b-form-group
                  :label="`${selectedSearch.label || ''} ${$t('common.from')}`"
                  label-for="from_date"
                  class="compact-form-group"
                >
                  <b-form-datepicker
                    v-model="from_date"
                    :placeholder="$t('common.select-date')"
                    :date-format-options="{
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                    }"
                    locale="en"
                    class="dark-datepicker compact-input"
                    today-button
                    reset-button
                    close-button
                  />
                </b-form-group>
              </b-col>
              <b-col md="6" cols="12" class="compact-col">
                <b-form-group
                  :label="`${selectedSearch.label || ''} ${$t('common.to')}`"
                  label-for="to_date"
                  class="compact-form-group"
                >
                  <b-form-datepicker
                    v-model="to_date"
                    :placeholder="$t('common.select-date')"
                    :date-format-options="{
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                    }"
                    locale="en"
                    class="dark-datepicker compact-input"
                    today-button
                    reset-button
                    close-button
                  />
                </b-form-group>
              </b-col>
            </b-row>
          </div>
        </b-collapse>

        <!-- Content Section -->
        <b-row class="compact-content-row">
          <b-col>
            <!-- List View -->
            <b-table
              v-if="viewMode === 'list' && !loading"
              :items="data"
              responsive
              :fields="bloodBottleTableColumns"
              primary-key="id"
              show-empty
              empty-text="No matching results found"
              class="position-relative compact-table"
            >
              <template #cell(status)="data">
                <b-badge
                  v-if="
                    data.item.status == 'available' &&
                    data.item.disposed_at === null
                  "
                  variant="success"
                  style="font-size: 1rem; color: #000"
                  >{{ data.item.status }}</b-badge
                >
                <b-badge
                  v-else-if="
                    data.item.status == 'dispensed' &&
                    data.item.disposed_at === null
                  "
                  variant="danger"
                  style="font-size: 1rem; color: #000"
                  >{{ data.item.status }}</b-badge
                >
                <b-badge
                  v-else-if="data.item.disposed_at !== null"
                  variant="warning"
                  style="font-size: 1rem; color: #000"
                  >Disposed</b-badge
                >
              </template>
              <template #cell(bottle_identifier)="data">
                <p style="font-size: 1.2rem; font-weight: bold">
                  {{ data.item.bottle_identifier }}
                </p>
              </template>
              <template #cell(abo)="data">
                <p style="font-size: 1.2rem; font-weight: bold">
                  {{ data.item.abo }}
                </p>
              </template>
              <template #cell(rh)="data">
                <p style="font-size: 1.2rem; font-weight: bold">
                  {{ data.item.rh }}
                </p>
              </template>
              <template #cell(production_date)="data">
                <p
                  style="
                    font-size: 1.2rem;
                    text-wrap: nowrap;
                    font-weight: bold;
                  "
                >
                  {{ data.item.production_date }}
                </p>
              </template>
              <template #cell(expiration_date)="data">
                <b-badge
                  :variant="getExpiryBadgeVariant(data.item.expiration_date)"
                  style="font-size: 16px; color: #000"
                >
                  <!-- <ClockIcon size="1.5x" /> -->
                  {{ data.item.expiration_date }}
                </b-badge>
              </template>
              <template #cell(inserted_at)="data">
                <p
                  style="
                    font-size: 1.2rem;
                    text-wrap: nowrap;
                    font-weight: bold;
                  "
                >
                  {{ data.item.inserted_at }}
                </p>
              </template>
              <template #cell(dispensed_at)="data">
                <div class="d-flex align-items-center">
                  <p
                    style="
                      font-size: 1.2rem;
                      text-wrap: nowrap;
                      font-weight: bold;
                    "
                  >
                    {{ data.item.dispensed_at }}
                  </p>
                </div>
              </template>
              <template #cell(disposed_at)="data">
                <b-button
                  v-if="
                    data.item.status !== 'dispensed' && !data.item.disposed_at
                  "
                  variant="warning"
                  size="sm"
                  style="
                    font-size: 1.2rem;
                    font-weight: bold;
                    color: #000 !important;
                  "
                  @click="showDisposeModal(data.item)"
                >
                  {{ $t("bloodBank.dispose") }}
                </b-button>
                <b-button
                  v-if="data.item.disposed_at"
                  variant="success"
                  size="sm"
                  :disabled="
                    !$store.state.auth.permissions.includes(
                      'assign-permission-to-user'
                    )
                  "
                  style="
                    font-size: 1.2rem;
                    font-weight: bold;
                    color: #000 !important;
                    text-wrap: nowrap;
                  "
                  @click="showRevertDisposalModal(data.item)"
                >
                  {{ data.item.disposed_at }}
                </b-button>
              </template>
              <!-- Column: Actions -->
              <template #cell(actions)="data">
                <b-dropdown
                  right
                  variant="outline-secondary"
                  size="sm"
                  class="no-nav"
                >
                  <template #button-content>
                    <feather-icon icon="MoreVerticalIcon" />
                  </template>

                  <b-dropdown-item
                    v-if="
                      $store.state.app.permissions.includes('get-bloodBottle')
                    "
                    @click.stop="show(data.item)"
                  >
                    <feather-icon icon="EditIcon" size="14" class="mr-50" />
                    {{ $t("common.edit") }}
                  </b-dropdown-item>

                  <b-dropdown-item
                    v-if="
                      $store.state.app.permissions.includes('get-bloodBottle')
                    "
                    class="text-danger"
                    @click.stop="deleteModel(data.item)"
                  >
                    <feather-icon icon="TrashIcon" size="14" class="mr-50" />
                    {{ $t("common.delete") }}
                  </b-dropdown-item>
                </b-dropdown>
              </template>
            </b-table>

            <!-- Card View -->
            <div
              v-else-if="viewMode === 'card' && !loading"
              class="blood-cards-container"
            >
              <!-- Empty State Placeholder -->
              <div v-if="data.length === 0" class="empty-state-card">
                <b-card class="text-center empty-placeholder-card">
                  <div class="empty-state-content">
                    <feather-icon
                      icon="DropletIcon"
                      size="48"
                      class="empty-icon"
                    />
                    <h5 class="mt-2 mb-1">
                      {{ $t("common.no-results") }}
                    </h5>
                    <p class="text-muted mb-0">
                      {{ $t("common.no-matching-results-found") }}
                    </p>
                  </div>
                </b-card>
              </div>
              <!-- Blood Bottle Cards -->
              <b-row v-else>
                <b-col
                  v-for="bottle in data"
                  :key="bottle.id"
                  cols="12"
                  md="6"
                  lg="4"
                  class="compact-card-col"
                >
                  <b-card class="blood-bottle-card h-100 compact-bottle-card">
                    <div class="card-body-card">
                      <div class="compact-card-body">
                        <div class="compact-card-header">
                          <div
                            class="d-flex justify-content-between align-items-start mb-1"
                          >
                            <h6
                              class="mb-0 font-weight-bold text-primary compact-bottle-id"
                            >
                              {{ bottle.bottle_identifier || "-" }}
                            </h6>
                            <b-badge
                              :variant="
                                bottle.status === 'available' &&
                                !bottle.disposed_at
                                  ? 'success'
                                  : bottle.status === 'dispensed' &&
                                    !bottle.disposed_at
                                  ? 'danger'
                                  : 'warning'
                              "
                              class="compact-badge"
                            >
                              {{
                                bottle.disposed_at
                                  ? "Disposed"
                                  : bottle.status || "-"
                              }}
                            </b-badge>
                          </div>
                        </div>

                        <div class="bottle-info-item compact-info-item">
                          <span class="info-label"
                            >{{ $t("bloodBank.abo") }}:</span
                          >
                          <span class="info-value font-weight-bold">{{
                            bottle.abo || "-"
                          }}</span>
                        </div>

                        <div class="bottle-info-item compact-info-item">
                          <span class="info-label"
                            >{{ $t("bloodBank.rh") }}:</span
                          >
                          <span class="info-value font-weight-bold">{{
                            bottle.rh || "-"
                          }}</span>
                        </div>

                        <div class="bottle-info-item compact-info-item">
                          <span class="info-label"
                            >{{ $t("bloodBank.prod-date") }}:</span
                          >
                          <span class="info-value">{{
                            bottle.production_date || "-"
                          }}</span>
                        </div>

                        <div class="bottle-info-item compact-info-item">
                          <span class="info-label"
                            >{{ $t("bloodBank.exp-date") }}:</span
                          >
                          <b-badge
                            :variant="
                              getExpiryBadgeVariant(bottle.expiration_date)
                            "
                            class="compact-badge"
                          >
                            {{ bottle.expiration_date || "-" }}
                          </b-badge>
                        </div>

                        <div class="bottle-info-item compact-info-item">
                          <span class="info-label"
                            >{{ $t("bloodBank.inserted-at") }}:</span
                          >
                          <span class="info-value">{{
                            bottle.inserted_at || "-"
                          }}</span>
                        </div>

                        <div
                          v-if="bottle.dispensed_at"
                          class="bottle-info-item compact-info-item"
                        >
                          <span class="info-label"
                            >{{ $t("bloodBank.dispensed-at") }}:</span
                          >
                          <span class="info-value">{{
                            bottle.dispensed_at
                          }}</span>
                        </div>
                      </div>

                      <div class="compact-card-footer">
                        <b-button
                          v-if="
                            bottle.status !== 'dispensed' && !bottle.disposed_at
                          "
                          variant="warning"
                          size="sm"
                          class="compact-action-button"
                          @click="showDisposeModal(bottle)"
                        >
                          <feather-icon icon="XIcon" size="14" />
                          {{ $t("bloodBank.dispose") }}
                        </b-button>
                        <b-button
                          v-if="bottle.disposed_at"
                          variant="success"
                          size="sm"
                          :disabled="
                            !$store.state.auth.permissions.includes(
                              'assign-permission-to-user'
                            )
                          "
                          class="compact-action-button"
                          @click="showRevertDisposalModal(bottle)"
                        >
                          <feather-icon icon="RotateCcwIcon" size="14" />
                          {{ $t("bloodBank.revert-disposal") }}
                        </b-button>
                        <b-dropdown
                          right
                          variant="outline-secondary"
                          size="sm"
                          class="compact-action-button"
                        >
                          <template #button-content>
                            <feather-icon icon="MoreVerticalIcon" size="14" />
                          </template>
                          <b-dropdown-item
                            v-if="
                              $store.state.app.permissions.includes(
                                'get-bloodBottle'
                              )
                            "
                            @click="show(bottle)"
                          >
                            <feather-icon
                              icon="EditIcon"
                              size="14"
                              class="mr-50"
                            />
                            {{ $t("common.edit") }}
                          </b-dropdown-item>
                          <b-dropdown-item
                            v-if="
                              $store.state.app.permissions.includes(
                                'get-bloodBottle'
                              )
                            "
                            class="text-danger"
                            @click="deleteModel(bottle)"
                          >
                            <feather-icon
                              icon="TrashIcon"
                              size="14"
                              class="mr-50"
                            />
                            {{ $t("common.delete") }}
                          </b-dropdown-item>
                        </b-dropdown>
                      </div>
                    </div>
                  </b-card>
                </b-col>
              </b-row>
            </div>

            <!-- Loading State -->
            <div v-if="loading" class="text-center compact-loading">
              <b-spinner label="Loading..." />
              <p class="mt-1 text-muted compact-loading-text">
                {{ $t("common.loading") }}
              </p>
            </div>
          </b-col>
        </b-row>
      </div>
      <div v-if="dataCount > 0" class="compact-pagination">
        <b-row class="compact-pagination-row">
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start compact-pagination-info"
          >
            <span class="text-muted compact-pagination-text">
              {{ $t("common.showing") }} {{ (page - 1) * take + 1 }}
              {{ $t("common.to") }} {{ Math.min(page * take, dataCount) }}
              {{ $t("common.of") }} {{ dataCount }}
            </span>
          </b-col>
          <!-- Pagination -->
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              v-model="page"
              :total-rows="dataCount"
              :per-page="take"
              first-number
              last-number
              size="sm"
              class="mb-0 compact-pagination-controls"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="16" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="16" />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>

    <IVF :data="data" type="Hospital" />
  </div>
</template>

<script>
import {
  BCard,
  BFormGroup,
  BButton,
  BFormCheckbox,
  BCol,
  BRow,
  BFormInput,
  BTable,
  BDropdown,
  BDropdownItem,
  BPagination,
  BSpinner,
  BFormTextarea,
  BFormSelect,
  BFormDatepicker,
  BBadge,
  BCardText,
  BCollapse,
  VBToggle,
  BButtonGroup,
  BCardBody,
} from "bootstrap-vue";
import vSelect from "vue-select";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import VuePhoneNumberInput from "vue-phone-number-input";
import "vue-phone-number-input/dist/vue-phone-number-input.css";
import Datepicker from "vuejs-datepicker";
import StatisticCardHorizontal from "@core/components/statistics-cards/StatisticCardHorizontal.vue";
import permissionChecker from "@/mixins/permissionChecker";
import DeleteModal from "@/components/modals/DeleteModal.vue";
import { ClockIcon } from "vue-feather-icons";

const IVF = () => import("@/components/printables/IVF.vue");
const Doctor = () => import("@/components/printables/Doctor.vue");

export default {
  directives: {
    "b-toggle": VBToggle,
  },
  components: {
    DeleteModal,
    BFormCheckbox,
    BCard,
    BFormGroup,
    BButton,
    BCol,
    BRow,
    BFormInput,
    BTable,
    BDropdown,
    BDropdownItem,
    BPagination,
    BSpinner,
    VuePhoneNumberInput,
    Datepicker,
    BFormTextarea,
    StatisticCardHorizontal,
    BBadge,
    IVF,
    Doctor,
    BFormSelect,
    BFormDatepicker,
    BCardText,
    vSelect,
    ClockIcon,
    BCollapse,
    BButtonGroup,
    BCardBody,
  },
  mixins: [permissionChecker],
  data() {
    return {
      take: 15,
      loading: true,
      dataCount: 0,
      data: [],
      EditForm: {
        bottle_identifier: "",
        abo: "",
        rh: "",
        form_date: "",
        to_date: "",
        _method: "PUT",
        inserted_at: "",
      },
      createMode: false,
      errorForm: {
        bottle_identifier: "",
        abo: "",
        rh: "",
        production_date: "",
        expiration_date: "",
      },
      params: {
        form: {},
        path: "",
      },
      bloodBottleTableColumns: [
        {
          key: "bottle_identifier",
          sortable: true,
          label: this.$t("bloodBank.bottle-identifier"),
        },
        {
          key: "abo",
          sortable: true,
          label: this.$t("bloodBank.abo"),
        },
        {
          key: "rh",
          sortable: true,
          label: this.$t("bloodBank.rh"),
        },
        {
          key: "status",
          sortable: true,
          label: this.$t("bloodBank.status"),
        },
        {
          key: "production_date",
          sortable: true,
          label: this.$t("bloodBank.prod-date"),
        },
        {
          key: "expiration_date",
          sortable: true,
          label: this.$t("bloodBank.exp-date"),
        },
        {
          key: "inserted_at",
          sortable: true,
          label: this.$t("bloodBank.inserted-at"),
        },
        {
          key: "dispensed_at",
          sortable: true,
          label: this.$t("bloodBank.dispensed-at"),
        },
        // {
        //   key: "dispose_action",
        //   label: this.$t("bloodBank.dispose-action"),
        //   // thStyle: { width: "100px" },
        // },
        {
          key: "disposed_at",
          sortable: true,
          label: this.$t("bloodBank.disposed-at"),
        },

        {
          key: "Actions",
          label: this.$t("common.actions"),
          thStyle: { width: "150px" },
        },
      ],

      aboOptions: ["A", "B", "AB", "O"],
      rhOptions: ["Positive", "Negative"],
      currentPage: 1,
      takeOptions: [10, 25, 50, 100],
      page: 1,
      IVF: {
        id: "IVF",
      },

      abo: "",
      rh: "",
      production_date: "",
      expiration_date: "",
      bottle_identifier: "",
      inserted_at: "",
      searchFromToBy: [
        {
          key: "created_at",
          label: this.$t("bloodBank.created_at"),
          from: "created_at-ge",
          to: "created_at-le",
        },
        {
          key: "production_date",
          label: this.$t("bloodBank.prod-date"),
          from: "production_date-ge",
          to: "production_date-le",
        },
        {
          key: "expiration_date",
          label: this.$t("bloodBank.exp-date"),
          from: "expiration_date-ge",
          to: "expiration_date-le",
        },
        {
          key: "inserted_at",
          label: this.$t("bloodBank.inserted-at"),
          from: "inserted_at-ge",
          to: "inserted_at-le",
        },
        {
          key: "dispensed_at",
          label: this.$t("bloodBank.dispensed-at"),
          from: "dispensed_at-ge",
          to: "dispensed_at-le",
        },
        {
          key: "reverted_at",
          label: this.$t("bloodBank.reverted-at"),
          from: "reverted_at-ge",
          to: "reverted_at-le",
        },
        {
          key: "disposed_at",
          label: this.$t("bloodBank.disposed-at"),
          from: "disposed_at-ge",
          to: "disposed_at-le",
        },
      ].map((item) => ({
        ...item,
        value: item,
      })),
      statusOptions: ["available", "dispensed", "disposed"],
      searchDate: "",
      selectedSearch: "",
      from_date: "",
      to_date: "",
      status: "",
      balances: [],
      bottleToDispose: null,
      viewMode: localStorage.getItem("bloodStoreView") || "card",
      isMounted: false,
    };
  },
  watch: {
    page() {
      this.getBloodBottles();
    },
    searchDate(newVal) {
      this.selectedSearch = newVal || "";
      if (this.isMounted) {
        this.searchBloodBottlesAndBalances();
      }
    },
    bottle_identifier() {
      if (this.isMounted) {
        this.searchBloodBottlesAndBalances();
      }
    },
    abo() {
      if (this.isMounted) {
        this.searchBloodBottlesAndBalances();
      }
    },
    rh() {
      if (this.isMounted) {
        this.searchBloodBottlesAndBalances();
      }
    },
    status() {
      if (this.isMounted) {
        this.searchBloodBottlesAndBalances();
      }
    },
    from_date() {
      if (this.isMounted && this.selectedSearch) {
        this.searchBloodBottlesAndBalances();
      }
    },
    to_date() {
      if (this.isMounted && this.selectedSearch) {
        this.searchBloodBottlesAndBalances();
      }
    },
    viewMode(newVal) {
      localStorage.setItem("bloodStoreView", newVal);
    },
  },
  mounted() {
    this.getBloodBottles();
    this.getbloodBottlesBalances();
    this.isMounted = true;
  },
  methods: {
    async searchBloodBottlesAndBalances() {
      await this.getBloodBottles();
      await this.getbloodBottlesBalances();
    },
    async getbloodBottlesBalances() {
      let url = "/bloodBottlesBalances";
      if (this.status) {
        url += `?status=${this.status}`;
      }
      const balances = await this.getList(url);
      this.balances = this.transformBalances(balances.data.data);
      console.log("balances", this.balances);
    },
    transformBalances(balances) {
      const result = [];

      balances.forEach((balance) => {
        // Find if the ABO type already exists in the result array
        const existing = result.find((item) => item.abo === balance.abo);

        if (existing) {
          // If it exists, add the RH type and total to it
          existing[balance.rh] = balance.total.toString();
        } else {
          // If it doesn't exist, create a new entry
          const newEntry = {
            abo: balance.abo,
            [balance.rh]: balance.total.toString(),
          };
          result.push(newEntry);
        }
      });

      return result;
    },
    formatDate(ivfDate) {
      const date = new Date(ivfDate);
      const year = date.getFullYear();
      let month = `${date.getMonth() + 1}`;
      let day = `${date.getDate()}`;

      if (month.length < 2) month = `0${month}`;
      if (day.length < 2) day = `0${day}`;

      return `${year}-${month}-${day}`;
    },
    show(item, mode) {
      console.log("item", item);
      if (mode === "create") {
        this.createMode = true;
        this.EditForm = {
          _method: "POST",
        };
      } else {
        this.createMode = false;
        this.EditForm.id = item.id;
        this.EditForm.bottle_identifier = item.bottle_identifier;
        this.EditForm.abo = item.abo;
        this.EditForm.rh = item.rh;
        this.EditForm.production_date = item.production_date;
        this.EditForm.expiration_date = item.expiration_date;
        this.EditForm.inserted_at = item.inserted_at;

        this.EditForm._method = "PUT";
      }
      this.$bvModal.show("modal-edit");
    },
    async getBloodBottles() {
      this.loading = true;
      // check if there are any null value in the obj we sending

      const requestTake =
        this.selectedSearch && (this.from_date || this.to_date) ? 1000 : 15;
      this.take = requestTake;

      let url = `/bloodBottles?page=${this.page}&take=${requestTake}&sort=-id&include[]=bloodTransfusions&include[]=bloodCompatibilities`;

      if (this.bottle_identifier) {
        url += `&filter[bottle_identifier]=${this.bottle_identifier}`;
      }
      if (this.abo) {
        url += `&filter[abo]=${this.abo}`;
      }
      if (this.rh) {
        url += `&filter[rh]=${this.rh}`;
      }
      if (this.from_date && this.selectedSearch && this.selectedSearch.from) {
        url += `&filter[${this.selectedSearch.from}]=${this.formatDate(
          this.from_date
        )}`;
      }
      if (this.to_date && this.selectedSearch && this.selectedSearch.to) {
        url += `&filter[${this.selectedSearch.to}]=${this.formatDate(
          this.to_date
        )}`;
      }
      if (this.status) {
        url += `&filter[status]=${this.status}`;
      }

      await this.getList(url);
      this.dataCount = this.all_data.total;
      this.data = this.all_data.data;
      this.loading = false;
    },

    deleteModel(item) {
      this.deleteForm = item;
      this.$bvModal.show("modal-delete");
    },
    deleteItem() {
      this.$store
        .dispatch("delete", `/bloodBottles/${this.deleteForm.id}`)
        .then(async (response) => {
          await this.searchBloodBottlesAndBalances();

          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: "Delete Success",
              icon: "success",
              variant: "info",
              text: `deleted ${this.deleteForm.bottle_identifier} successfully`,
            },
          });
        })
        .catch((error) => {
          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: "Error",
              variant: "danger",
              text: `Error deleting ${this.deleteForm.bottle_identifier}`,
            },
          });
        });
    },

    submitItem() {
      let count = 0;
      this.loading = true;
      this.errorForm.bottle_identifier = "";
      this.errorForm.abo = "";
      this.errorForm.rh = "";
      this.errorForm.production_date = "";
      this.errorForm.expiration_date = "";

      // Validate required fields first (before deleting empty keys from EditForm)
      if (!this.EditForm.bottle_identifier) {
        count++;
        this.errorForm.bottle_identifier =
          "The bottle identifier field is required.";
      }
      if (!this.EditForm.abo) {
        count++;
        this.errorForm.abo = "The ABO field is required.";
      }
      if (!this.EditForm.rh) {
        count++;
        this.errorForm.rh = "The RH field is required.";
      }
      if (!this.EditForm.production_date) {
        count++;
        this.errorForm.production_date =
          "The production date field is required.";
      }
      if (!this.EditForm.expiration_date) {
        count++;
        this.errorForm.expiration_date =
          "The expiration date field is required.";
      }

      if (count > 0) {
        this.loading = false;
        return false;
      }

      for (const item in this.errorForm) {
        this.errorForm[item] = "";
        if (this.EditForm[item] == "" || this.EditForm[item] == null)
          delete this.EditForm[item];
      }

      console.log("editForm", this.EditForm);

      this.EditForm.production_date = this.formatDate(
        this.EditForm.production_date
      );
      this.EditForm.expiration_date = this.formatDate(
        this.EditForm.expiration_date
      );
      this.EditForm.inserted_at = this.formatDate(this.EditForm.inserted_at);

      if (this.createMode) {
        (this.params.form = this.EditForm),
          (this.params.path = "/bloodBottles"),
          this.$store
            .dispatch("create", this.params)
            .then(async (response) => {
              this.$bvModal.hide("modal-edit");
              this.loading = false;
              console.log("this.expenses", this.expenses);
              await this.searchBloodBottlesAndBalances();
            })
            .catch((error) => {
              this.loading = false;
              if (error.response.status === 400) {
                for (const item in error.response.data) {
                  this.$toast({
                    component: ToastificationContent,
                    position: "top-right",
                    props: {
                      title: "validation error",
                      variant: "danger",
                      text: error.response.data[item][0],
                    },
                  });
                }
              }
            });
      } else {
        (this.params.form = this.EditForm),
          (this.params.path = `/bloodBottles/${this.EditForm.id}`),
          this.$store
            .dispatch("update", this.params)
            .then(async (response) => {
              this.$bvModal.hide("modal-edit");
              this.loading = false;
              await this.searchBloodBottlesAndBalances();
            })
            .catch((error) => {
              this.loading = false;
              if (error.response.status === 400) {
                for (const item in error.response.data) {
                  this.$toast({
                    component: ToastificationContent,
                    position: "top-right",
                    props: {
                      title: "validation error",
                      variant: "danger",
                      text: error.response.data[item][0],
                    },
                  });
                }
              }
            });
      }
    },
    getExpiryBadgeVariant(expiryDate) {
      const today = new Date();
      const expirationDate = new Date(expiryDate);
      const timeDifference = expirationDate - today;

      const daysDifference = timeDifference / (1000 * 60 * 60 * 24);

      console.log(
        `daysDifference: ${daysDifference} expiryDate: ${expiryDate}`
      );
      if (daysDifference > 60) {
        return "success"; // More than 2 months
      }
      if (daysDifference > 0 && daysDifference <= 60) {
        return "warning"; // Between today and 2 months
      }
      return "danger"; // Today or in the past
    },
    showDisposeModal(bottle) {
      this.bottleToDispose = bottle;
      this.$bvModal.show("modal-dispose");
    },
    async disposeBottle() {
      this.loading = true;
      try {
        await this.$store.dispatch("put", {
          path: `disposeBloodBottle/${this.bottleToDispose.id}`,
          form: {},
        });

        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: this.$t("common.success"),
            icon: "CheckIcon",
            variant: "success",
            text: this.$t("bloodBank.dispose-success"),
          },
        });

        this.$bvModal.hide("modal-dispose");
        // Refresh the blood bottles list
        await this.searchBloodBottlesAndBalances();
        this.loading = false;
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: this.$t("common.error"),
            icon: "AlertTriangleIcon",
            variant: "danger",
            text:
              error.response?.data?.message || this.$t("common.error-occurred"),
          },
        });
        this.loading = false;
      }
    },
    showRevertDisposalModal(bottle) {
      this.bottleToDispose = bottle;
      this.$bvModal.show("modal-revert-disposal");
    },
    async revertDisposal() {
      this.loading = true;
      try {
        await this.$store.dispatch("put", {
          path: `revertBloodBottleDisposal/${this.bottleToDispose.id}`,
          form: {},
        });

        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: this.$t("common.success"),
            icon: "CheckIcon",
            variant: "success",
            text: this.$t("bloodBank.revert-disposal-success"),
          },
        });

        this.$bvModal.hide("modal-revert-disposal");
        // Refresh the blood bottles list
        await this.searchBloodBottlesAndBalances();
        this.loading = false;
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: this.$t("common.error"),
            icon: "AlertTriangleIcon",
            variant: "danger",
            text:
              error.response?.data?.message || this.$t("common.error-occurred"),
          },
        });
        this.loading = false;
      }
    },
  },
};
</script>
<style lang="scss" scoped>
/* Blood balance card styling */
.blood-balance-card {
  background: linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.blood-balance-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

/* Collapse toggle button styles */
.collapsed > .when-open,
.not-collapsed > .when-closed {
  display: none;
}

/* CSS */
.button-preview {
  align-items: center;
  appearance: none;
  background-color: #fff;
  border: 1px solid #dbdbdb;
  border-radius: 0.375em;
  box-shadow: none;
  box-sizing: border-box;
  color: #363636;
  cursor: pointer;
  display: inline-flex;
  font-family: BlinkMacSystemFont, -apple-system, "Segoe UI", Roboto, Oxygen,
    Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", Helvetica,
    Arial, sans-serif;
  font-size: 1rem;
  height: 2.5em;
  justify-content: center;
  line-height: 1.5;
  padding: calc(0.5em - 1px) 1em;
  position: relative;
  text-align: center;
  user-select: none;
  -webkit-user-select: none;
  touch-action: manipulation;
  vertical-align: top;
  white-space: nowrap;
  margin-bottom: 3px;
  margin-right: 10px;
}

.button-preview:active {
  border-color: #4a4a4a;
  outline: 0;
}

.button-preview:focus {
  border-color: #485fc7;
  outline: 0;
}

.button-preview:hover {
  border-color: #b5b5b5;
  background: rgb(245, 241, 241);
}

.button-preview:focus:not(:active) {
  box-shadow: rgba(72, 95, 199, 0.25) 0 0 0 0.125em;
}

.modify {
  height: 6rem;
  font-size: 2rem;
  align-items: center;
}

.modify:hover {
  box-shadow: rgba(0, 0, 0, 0.35) 0px 5px 15px;
}

// Compact styles - no margins/padding
.compact-card {
  margin: 0;
  border: none;
}

.main-padding {
  padding: 1rem;
}

.compact-container {
  padding: 0.5rem;
}

.compact-row {
  margin: 0;
  margin-bottom: 0.5rem;

  &:last-child {
    margin-bottom: 0;
  }
}

.compact-header {
  margin-bottom: 0.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e0e0e0;
}

.compact-title {
  margin: 0;
}

.compact-balance-cards {
  margin-bottom: 0.5rem;
}

.compact-balance-card {
  background: linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  padding: 0.75rem;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  transition: all 0.2s ease;
  margin: 0;

  &:hover {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
}

.compact-balance-abo {
  font-size: 1.5rem;
  font-weight: bolder;
  color: #7367f0;
  margin: 0;
}

.compact-balance-row {
  margin-bottom: 0.5rem;
  font-size: 0.9rem;

  &:last-child {
    margin-bottom: 0;
  }
}

.compact-balance-label {
  font-weight: 600;
  color: #666;
}

.compact-balance-badge {
  font-size: 1rem;
  font-weight: bold;
  min-width: 40px;
}

.compact-filter-toggle {
  margin-bottom: 0.5rem;
}

.compact-filters-section {
  padding: 0.5rem 0;
}

.compact-filters {
  margin-bottom: 0.5rem;
}

.compact-col {
  padding: 0.25rem;
}

.compact-form-group {
  margin-bottom: 0.5rem;

  ::v-deep label {
    margin-bottom: 0.25rem;
  }
}

.compact-input {
  font-size: 0.875rem;
}

.compact-content-row {
  margin: 0.5rem 0;
}

.view-toggle-buttons {
  display: flex;
  align-items: center;
}

// Compact table
.compact-table {
  margin: 0;

  ::v-deep th,
  ::v-deep td {
    padding: 0.5rem 0.75rem;
  }

  ::v-deep thead th {
    font-weight: 600;
    padding: 0.5rem 0.75rem;
  }
}

// Blood bottle card view styles - compact
.blood-cards-container {
  min-height: 150px;
  margin: 0;
}

.compact-card-col {
  padding: 0.25rem;
  margin: 0;
}

.compact-bottle-card {
  border: 1px solid #e0e0e0;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  margin: 0;
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  height: 100%;

  &:hover {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  .compact-card-header {
    border-bottom: 1px solid #f0f0f0;
    padding-bottom: 0.5rem;
    margin-bottom: 0.5rem;
    flex-shrink: 0;
  }

  .compact-bottle-id {
    margin: 0;
  }
  .card-body-card {
    display: flex !important;
    flex-direction: column !important;
    justify-content: space-between !important;
    height: 100% !important;
  }
  .compact-card-body {
    padding: 0.5rem 0;
    display: flex;
    flex-direction: column;
    flex-grow: 1;
  }

  .compact-card-footer {
    border-top: 1px solid #f0f0f0;
    padding-top: 0.5rem;
    margin-top: auto;
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
    flex-shrink: 0;
  }

  .bottle-info-item {
    display: flex;
    flex-wrap: wrap;
    margin-bottom: 0.5rem;
    gap: 0.5rem;

    &:last-child {
      margin-bottom: 0;
    }

    .info-label {
      font-weight: 600;
      color: #666;
      margin-bottom: 0.15rem;
    }

    .info-value {
      color: #333;
      word-break: break-word;
    }
  }

  .compact-badge {
    font-size: 0.75rem;
  }

  .compact-action-button {
    flex: 1;
    min-width: fit-content;
    font-size: 0.8rem;
    padding: 0.375rem;
  }
}

// Empty state placeholder
.empty-state-card {
  padding: 2rem 1rem;
}

.empty-placeholder-card {
  border: 2px dashed #e0e0e0;
  background-color: #f8f9fa;
  padding: 3rem 2rem;
}

.empty-state-content {
  .empty-icon {
    color: #adb5bd;
    margin-bottom: 1rem;
  }
}

.compact-loading {
  padding: 1rem 0;
}

.compact-loading-text {
  margin: 0.5rem 0 0 0;
}

.compact-pagination {
  padding: 0.5rem;
  margin: 0;
}

.compact-pagination-row {
  margin: 0;
}

.compact-pagination-info {
  padding: 0.25rem;
}

.compact-pagination-text {
  font-size: 0.8rem;
}

.compact-pagination-controls {
  ::v-deep .page-link {
    padding: 0.25rem 0.5rem;
    font-size: 0.8rem;
  }

  ::v-deep .page-item {
    margin: 0 0.1rem;
  }
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
